import { execFile } from "node:child_process";
import { randomUUID } from "node:crypto";
import { constants } from "node:fs";
import {
  lstat,
  mkdir,
  mkdtemp,
  open,
  readdir,
  rename,
  rm,
  writeFile,
} from "node:fs/promises";
import path from "node:path";
import { isDeepStrictEqual, promisify } from "node:util";
import { contentHash, type Files, type UpgradePlan } from "./planner.js";

const exec = promisify(execFile);
export async function git(dir: string, args: string[]): Promise<string> {
  return (
    await exec("git", ["-C", dir, ...args], { maxBuffer: 8 * 1024 * 1024 })
  ).stdout;
}

/** Read and validate the same open file; never follow a final-component symlink. */
async function readRegularFile(file: string): Promise<string> {
  const handle = await open(file, constants.O_RDONLY | constants.O_NOFOLLOW);
  try {
    const stat = await handle.stat();
    if (!stat.isFile() || stat.size > 5 * 1024 * 1024)
      throw new Error(`${file}: expected a regular file no larger than 5 MB.`);
    return await handle.readFile("utf8");
  } finally {
    await handle.close();
  }
}

/** Capture the full routing/configuration inventory, including files not changed by the upgrade. */
export async function readProject(dir: string): Promise<Files> {
  const entries: [string, string][] = [];
  async function read(relative: string, optional = false): Promise<void> {
    const absolute = path.join(dir, relative);
    let stat;
    try {
      stat = await lstat(absolute);
    } catch (error) {
      if (optional && (error as NodeJS.ErrnoException).code === "ENOENT")
        return;
      throw error;
    }
    if (stat.isSymbolicLink())
      throw new Error(`${relative}: symlinks require manual review.`);
    if (stat.isDirectory()) {
      for (const name of (await readdir(absolute)).sort())
        await read(`${relative}/${name}`);
    } else if (stat.isFile()) {
      if (stat.size > 5 * 1024 * 1024)
        throw new Error(
          `${relative}: file is too large for a conservative upgrade.`
        );
      entries.push([relative, await readRegularFile(absolute)]);
    } else throw new Error(`${relative}: expected a regular file.`);
  }
  for (const file of [
    "config",
    "modules",
    "zuplo.jsonc",
    "package.json",
    "package-lock.json",
    "npm-shrinkwrap.json",
    "pnpm-lock.yaml",
    "yarn.lock",
    ".zuplo-template.json",
  ])
    await read(file, true);
  return Object.fromEntries(entries);
}

export async function requireCleanProject(
  dir: string,
  plan: UpgradePlan
): Promise<void> {
  if (
    (await git(dir, ["status", "--porcelain", "--untracked-files=all"])).trim()
  ) {
    throw new Error(
      "Commit or stash your changes before applying an AI Gateway upgrade. Preview is available without a clean working tree."
    );
  }
  for (const file of Object.keys(plan.after).filter(
    (f) => plan.before[f] !== undefined
  )) {
    await git(dir, ["ls-files", "--error-unmatch", "--", file]);
  }
}

/** Atomic per-file replacement, durable originals, and rollback that never overwrites a concurrent edit. */
export async function applyPlan(
  dir: string,
  plan: UpgradePlan,
  backupRoot: string,
  hooks: { beforeReplace?: (file: string, index: number) => Promise<void> } = {}
): Promise<string> {
  if (plan.blockers.length)
    throw new Error("Blocked upgrade plans cannot be applied.");
  await mkdir(backupRoot, { recursive: true, mode: 0o700 });
  const lock = path.join(backupRoot, `${contentHash(path.resolve(dir))}.lock`);
  try {
    await writeFile(lock, String(process.pid), { flag: "wx", mode: 0o600 });
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code !== "EEXIST") throw error;
    throw new Error(
      `Another upgrade may be running. Inspect ${lock} before removing a stale lock.`
    );
  }
  let backup: string | undefined;
  const written: string[] = [];
  const temporary: string[] = [];
  try {
    await requireCleanProject(dir, plan);
    if (!isDeepStrictEqual(await readProject(dir), plan.before))
      throw new Error(
        "Project files changed after preview. Run the preview again."
      );
    backup = await mkdtemp(path.join(backupRoot, "ai-gateway-"));
    const journal = Object.keys(plan.after).map((file) => ({
      file,
      existed: plan.before[file] !== undefined,
      before:
        plan.before[file] === undefined ? null : contentHash(plan.before[file]),
      after: contentHash(plan.after[file]),
    }));
    await writeFile(
      path.join(backup, "journal.json"),
      JSON.stringify(
        {
          project: dir,
          release: plan.release,
          target: plan.target,
          files: journal,
        },
        null,
        2
      ),
      { mode: 0o600, flush: true }
    );
    for (const file of Object.keys(plan.after)) {
      if (plan.before[file] !== undefined) {
        await mkdir(path.dirname(path.join(backup, file)), {
          recursive: true,
          mode: 0o700,
        });
        await writeFile(path.join(backup, file), plan.before[file], {
          mode: 0o600,
          flush: true,
        });
      }
    }
    const expected = { ...plan.before };
    for (const [index, file] of Object.keys(plan.after).entries()) {
      await hooks.beforeReplace?.(file, index);
      if (!isDeepStrictEqual(await readProject(dir), expected))
        throw new Error(
          "Project files changed while applying the upgrade. Stopping."
        );
      const destination = path.join(dir, file);
      const mode =
        plan.before[file] === undefined
          ? 0o644
          : (await lstat(destination)).mode;
      const temp = `${destination}.zuplo-upgrade-${randomUUID()}.tmp`;
      temporary.push(temp);
      await writeFile(temp, plan.after[file], {
        mode,
        flag: "wx",
        flush: true,
      });
      await rename(temp, destination);
      written.push(file);
      expected[file] = plan.after[file];
    }
    return backup;
  } catch (error) {
    const recovery: string[] = [];
    for (const file of written.reverse()) {
      try {
        const destination = path.join(dir, file);
        if ((await readRegularFile(destination)) !== plan.after[file]) {
          recovery.push(file);
          continue;
        }
        if (plan.before[file] === undefined) await rm(destination);
        else {
          const temp = `${destination}.zuplo-upgrade-${randomUUID()}.tmp`;
          temporary.push(temp);
          await writeFile(temp, plan.before[file], {
            mode: (await lstat(destination)).mode,
            flag: "wx",
            flush: true,
          });
          await rename(temp, destination);
        }
      } catch {
        recovery.push(file);
      }
    }
    throw new Error(
      `${error instanceof Error ? error.message : String(error)}${backup ? ` Originals and recovery journal: ${backup}.` : ""}${recovery.length ? ` Restore manually after reviewing concurrent edits: ${recovery.join(", ")}.` : ""}`
    );
  } finally {
    for (const temp of temporary)
      await rm(temp, { force: true }).catch(() => {});
    await rm(lock, { force: true }).catch(() => {});
  }
}
